import React, { useState, useEffect } from 'react';
import { User } from '../../../common/Interface/User';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import DefaultLayout from '../../../layout/DefaultLayout';
import { sortUsersByPriority } from '../../../utils/UserSortUtils';

// GraphQL queries and mutations (to be added to graphql/mutations.ts)
import { GET_USERS, CREATE_USER, UPDATE_USER, DELETE_USER } from '../../../graphql/queries';

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();

  // 選択中のユーザー（編集対象）
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 編集モードの状態管理
  const [isEditing, setIsEditing] = useState(false);

  // 新規作成モードの状態管理
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    sub: '',
    email: '',
    isPermanentEmployee: false,
    externalCompany: '',
    bulkDownloadTarget: false,
    monitoringTarget: false,
  });

  // GraphQL hooks
  const { data, loading, refetch } = useQuery(GET_USERS);
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [deleteUser] = useMutation(DELETE_USER);

  const users = sortUsersByPriority(data?.getAllUsers || []);

  // 管理者権限チェック
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const isAdmin = attributes['custom:role'] === 'manager';
        console.log('User is admin:', isAdmin);

        if (!isAdmin) {
          navigate('/timecard');
          return;
        }
      } catch (error) {
        console.error('認証エラー:', error);
        navigate('/');
      }
    };
    checkUserRole();
  }, [navigate]);

  // ユーザー作成
  const handleCreateUser = async () => {
    try {
      await createUser({
        variables: {
          input: formData,
        },
      });
      await refetch();
      resetForm();
      setIsCreating(false);
    } catch (error) {
      console.error('ユーザー作成エラー:', error);
    }
  };

  // ユーザー更新
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await updateUser({
        variables: {
          id: selectedUser.sequenceId.toString(),
          input: formData,
        },
      });
      await refetch();
      resetForm();
      setIsEditing(false);
    } catch (error) {
      console.error('ユーザー更新エラー:', error);
    }
  };

  // ユーザー削除
  const handleDeleteUser = async (userId: number) => {
    const userToDelete = users.find((user: User) => user.sequenceId === userId);
    const userName = userToDelete
      ? `${userToDelete.lastName} ${userToDelete.firstName || ''}`
      : 'このユーザー';

    if (window.confirm(`本当に ${userName} を削除しますか？この操作は取り消せません。`)) {
      try {
        await deleteUser({
          variables: {
            id: userId.toString(),
          },
        });
        await refetch();
      } catch (error) {
        console.error('ユーザー削除エラー:', error);
      }
    }
  };

  // フォームリセット
  const resetForm = () => {
    setFormData({
      lastName: '',
      firstName: '',
      sub: '',
      email: '',
      isPermanentEmployee: false,
      externalCompany: '',
      bulkDownloadTarget: false,
      monitoringTarget: false,
    });
    setSelectedUser(null);
  };

  // 編集モード開始
  const startEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      lastName: user.lastName,
      firstName: user.firstName || '',
      sub: user.sub,
      email: user.email,
      isPermanentEmployee: user.isPermanentEmployee,
      externalCompany: user.externalCompany || '',
      bulkDownloadTarget: user.bulkDownloadTarget,
      monitoringTarget: user.monitoringTarget,
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  // 作成モード開始
  const startCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsEditing(false);
  };

  // フォーム送信
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreating) {
      handleCreateUser();
    } else if (isEditing) {
      handleUpdateUser();
    }
  };

  return (
    <DefaultLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">ユーザー管理</h1>

        {/* 注意文 */}
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6">
          <p>
            <strong>注意:</strong>{' '}
            ユーザー認証は別システムで管理されます。ここではユーザー情報の基本的な管理のみを行います。
          </p>
        </div>

        {/* 新規作成ボタン */}
        <div className="mb-4">
          <button
            onClick={startCreate}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            新規ユーザー追加
          </button>
        </div>

        {/* フォーム */}
        {(isCreating || isEditing) && (
          <div className="bg-gray-100 p-4 rounded mb-6">
            <h3 className="text-lg font-semibold mb-4">
              {isCreating ? '新規ユーザー作成' : 'ユーザー編集'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">姓</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">名</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Sub</label>
                <input
                  type="text"
                  value={formData.sub}
                  onChange={(e) => setFormData({ ...formData, sub: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">所属会社</label>
                <input
                  type="text"
                  value={formData.externalCompany}
                  onChange={(e) => setFormData({ ...formData, externalCompany: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPermanentEmployee}
                    onChange={(e) =>
                      setFormData({ ...formData, isPermanentEmployee: e.target.checked })
                    }
                    className="mr-2"
                  />
                  正社員
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.bulkDownloadTarget}
                    onChange={(e) =>
                      setFormData({ ...formData, bulkDownloadTarget: e.target.checked })
                    }
                    className="mr-2"
                  />
                  一括DL対象
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.monitoringTarget}
                    onChange={(e) =>
                      setFormData({ ...formData, monitoringTarget: e.target.checked })
                    }
                    className="mr-2"
                  />
                  監視対象
                </label>
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  {isCreating ? '作成' : '更新'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsCreating(false);
                    setIsEditing(false);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ユーザー一覧 */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">ユーザー一覧</h3>
            {loading ? (
              <p>読み込み中...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="px-4 py-2 bg-blue-900 text-white">
                      <th className="px-4 py-2 text-left text-sm font-medium">ID</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">姓名</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">メール</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">会社</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">属性</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user: User) => (
                      <tr key={user.sequenceId} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {user.sequenceId}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">{user.lastName} {user.firstName}</div>
                          <div className="text-xs text-gray-500 truncate max-w-32" title={user.sub}>
                            {user.sub}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="truncate max-w-48" title={user.email}>
                            {user.email}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="truncate max-w-32" title={user.externalCompany}>
                            {user.externalCompany}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {user.isPermanentEmployee && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                正社員
                              </span>
                            )}
                            {user.bulkDownloadTarget && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                一括DL
                              </span>
                            )}
                            {user.monitoringTarget && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                監視
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <div className="flex flex-col sm:flex-row gap-1">
                            <button
                              onClick={() => startEdit(user)}
                              className="text-indigo-600 hover:text-indigo-900 px-2 py-1 rounded text-xs"
                            >
                              編集
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.sequenceId)}
                              className="text-red-600 hover:text-red-900 px-2 py-1 rounded text-xs"
                            >
                              削除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default UserManagementPage;
